"use client";

import type { FC } from "react";
import {
  Flex,
  FormRow,
  Grid,
  NumberField,
  RadioCards,
  Select,
  Spacing,
  Text,
} from "@huub-web/ui";
import {
  InputConfiguration,
  FormInputDisplayConfiguration,
} from "@huub-web/ui/services/inputs";
import { useTranslation } from "react-i18next";

import { useMaximumMortgageWizard } from "../../../hooks/useMaximumMortgageWizard";
import { BaseSlide, type BaseSlideProps } from "./BaseSlide";
import { Illustration } from "../../Illustration";
import { twoColumnGridStyle } from "./BaseSlide.css";

type CurrentHouseSlideProps = BaseSlideProps;

/**
 * Current house slide component for the mortgage wizard
 * Handles current house ownership and related financial information
 *
 * ⚠️ When adding/removing conditional fields, update filterMaximumMortgageWizardValues.ts
 */
export const CurrentHouseSlide: FC<CurrentHouseSlideProps> = () => {
  const { t } = useTranslation();
  const { state, updateFormValues, bankOptions, banksLoading } =
    useMaximumMortgageWizard();
  const { formValues } = state;
  const plurality = formValues.hasPartner ? "plural" : "singular";

  return (
    <BaseSlide
      slideKey="currentHouse"
      title={t(`maxMortgageWizard.currentHouse.title.${plurality}`)}
      description={t(`maxMortgageWizard.currentHouse.description.${plurality}`)}
    >
      <FormRow
        id="wizard__hasCurrentHouse"
        data-field-key="hasCurrentHouse"
        variant="vertical"
      >
        <RadioCards
          name="hasCurrentHouse"
          value={
            formValues.hasCurrentHouse === true
              ? "yes"
              : formValues.hasCurrentHouse === false
                ? "no"
                : ""
          }
          onChange={(value) =>
            updateFormValues({ hasCurrentHouse: value === "yes" })
          }
          options={[
            {
              value: "yes",
              label: t("maxMortgageWizard.currentHouse.yes"),
              leading: <Illustration name="myHouse" size="xs" />,
            },
            {
              value: "no",
              label: t("maxMortgageWizard.currentHouse.no"),
              leading: <Illustration name="default" size="xs" />,
            },
          ]}
          validation={["required"]}
        />
      </FormRow>

      {formValues.hasCurrentHouse && (
        <>
          <Spacing />
          <Flex direction="column" gap="xs">
            <Text as="h3" variant="displayS" bold>
              {t("maxMortgageWizard.currentHouse.expectedSalePrice")}
            </Text>
          </Flex>
          <Grid variant="none" className={twoColumnGridStyle}>
            <FormRow
              id="wizard__currentHouseSellingPrice"
              data-field-key="currentHouseSellingPrice"
              variant="vertical"
            >
              <NumberField
                {...InputConfiguration.money.props}
                leading={FormInputDisplayConfiguration.money.leading}
                value={
                  formValues.currentHouseSellingPrice &&
                  formValues.currentHouseSellingPrice !== ""
                    ? Number(formValues.currentHouseSellingPrice)
                    : undefined
                }
                onChange={(value) =>
                  updateFormValues({
                    currentHouseSellingPrice: value?.toString() ?? "",
                  })
                }
                validation={["required"]}
                name={t("maxMortgageWizard.currentHouse.expectedSalePriceName")}
                label={t(
                  "maxMortgageWizard.currentHouse.expectedSalePriceLabel",
                )}
              />
            </FormRow>
          </Grid>
          <Spacing />
          <Flex direction="column" gap="xs">
            <Text as="h3" variant="displayS" bold>
              {t("maxMortgageWizard.currentHouse.currentMortgageDebt")}
            </Text>
          </Flex>
          <Grid variant="none" className={twoColumnGridStyle}>
            <FormRow
              id="wizard__currentHouseMortgageDebt"
              data-field-key="currentHouseMortgageDebt"
              variant="vertical"
            >
              <NumberField
                {...InputConfiguration.money.props}
                leading={FormInputDisplayConfiguration.money.leading}
                value={
                  formValues.currentHouseMortgageDebt &&
                  formValues.currentHouseMortgageDebt !== ""
                    ? Number(formValues.currentHouseMortgageDebt)
                    : undefined
                }
                onChange={(value) =>
                  updateFormValues({
                    currentHouseMortgageDebt: value?.toString() ?? "",
                  })
                }
                validation={["required"]}
                name={t("maxMortgageWizard.currentHouse.remainingMortgageName")}
                label={t(
                  "maxMortgageWizard.currentHouse.remainingMortgageLabel",
                )}
              />
            </FormRow>

            <FormRow
              id="wizard__currentHouseMortgageLender"
              data-field-key="currentHouseMortgageLender"
              variant="vertical"
            >
              <Select
                search
                value={formValues.currentHouseMortgageLender ?? ""}
                onChange={(value) =>
                  updateFormValues({
                    currentHouseMortgageLender: value?.toString() ?? "",
                  })
                }
                options={banksLoading ? [] : bankOptions}
                validation={["required"]}
                display={
                  formValues.currentHouseMortgageLender === "other"
                    ? t("maxMortgageWizard.lenderOther")
                    : (formValues.currentHouseMortgageLender ?? "")
                }
                name={t("maxMortgageWizard.lenderName")}
                label={t("maxMortgageWizard.lenderLabel")}
              />
            </FormRow>
          </Grid>
        </>
      )}
    </BaseSlide>
  );
};
